import { useEffect, useMemo, useState } from "react";
import { Newspaper, SearchX } from "lucide-react";

import {
  ContentGrid,
  ContentGridSkeleton,
  EmptyState,
  FilterBarSkeleton,
} from "./ContentViews";
import { Button } from "@/components/ui/button";
import { Section } from "./Section";
import { Skeleton } from "@/components/ui/skeleton";
import type { LocalizedContent } from "@/lib/cms.types";
import { useT } from "@/lib/i18n-dict";

type Props = {
  items: LocalizedContent[];
  base: string;
  locale: string;
  emptyLabel: string;
  logoMode?: boolean;
  /** Show the year filter (dated content such as blog and news). */
  showYears?: boolean;
  searchPlaceholder?: string;
  /** Optional category chips (blog). */
  categories?: { id: string; slug: string; name: string; itemIds: string[] }[];
};

const chip =
  "rounded-full border px-4 py-1.5 text-sm transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none";

const PAGE_SIZE = 9;

/** Full-section loading state for hub pages (route pendingComponent). */
export function ContentHubSkeleton() {
  return (
    <Section>
      <FilterBarSkeleton />
      <div className="mt-8">
        <ContentGridSkeleton count={6} />
      </div>
    </Section>
  );
}

/** Client-side search + year filtering over a CMS content list. */
export function FilteredContentGrid({
  items,
  base,
  locale,
  emptyLabel,
  logoMode = false,
  showYears = true,
  searchPlaceholder = "Search…",
  categories = [],
}: Props) {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<string>("all");
  const usedCategories = useMemo(() => {
    const ids = new Set(items.map((item) => item.id));
    return categories.filter((c) => c.itemIds.some((id) => ids.has(id)));
  }, [categories, items]);
  const [year, setYear] = useState<string>("all");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const t = useT();

  const years = useMemo(() => {
    const set = new Set<string>();
    for (const item of items) {
      if (item.published_at) set.add(new Date(item.published_at).getFullYear().toString());
    }
    return [...set].sort((a, b) => Number(b) - Number(a));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const activeCat = usedCategories.find((c) => c.id === category);
    const list = items.filter((item) => {
      if (activeCat && !activeCat.itemIds.includes(item.id)) return false;
      if (showYears && year !== "all") {
        const itemYear = item.published_at
          ? new Date(item.published_at).getFullYear().toString()
          : "";
        if (itemYear !== year) return false;
      }
      if (!q) return true;
      return `${item.title} ${item.excerpt}`.toLowerCase().includes(q);
    });
    if (!showYears) return list;
    return [...list].sort(
      (a, b) =>
        new Date(b.published_at ?? 0).getTime() - new Date(a.published_at ?? 0).getTime(),
    );
  }, [items, query, year, showYears, category, usedCategories]);

  const [filtering, setFiltering] = useState(false);

  useEffect(() => {
    setVisible(PAGE_SIZE);
    if (!query && year === "all" && category === "all") return;
    setFiltering(true);
    const timer = setTimeout(() => setFiltering(false), 350);
    return () => clearTimeout(timer);
  }, [query, year, category]);

  const shown = filtered.slice(0, visible);
  const showYearBar = showYears && years.length > 1;

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <label className="relative w-full md:max-w-sm">
          <span className="sr-only">{t(searchPlaceholder)}</span>
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(searchPlaceholder)}
            className="w-full rounded-full border border-border bg-card px-5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:border-primary/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          />
        </label>

        {showYearBar ? (
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant={year === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setYear("all")}
              aria-pressed={year === "all"}
              className={chip}
            >
              {t("All years")}
            </Button>
            {years.map((value) => (
              <Button
                key={value}
                type="button"
                variant={year === value ? "default" : "outline"}
                size="sm"
                onClick={() => setYear(value)}
                aria-pressed={year === value}
                className={chip}
              >
                {value}
              </Button>
            ))}
          </div>
        ) : null}
      </div>

      {usedCategories.length ? (
        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label={t("Categories")}>
          <Button
            type="button"
            variant={category === "all" ? "default" : "outline"}
            size="sm"
            onClick={() => setCategory("all")}
            aria-pressed={category === "all"}
            className={chip}
          >
            {t("All topics")}
          </Button>
          {usedCategories.map((c) => (
            <Button
              key={c.id}
              type="button"
              variant={category === c.id ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory(c.id)}
              aria-pressed={category === c.id}
              className={chip}
            >
              {c.name}
            </Button>
          ))}
        </div>
      ) : null}

      <p className="mt-4 text-sm text-muted-foreground" aria-live="polite">
        {t("Showing")} {shown.length} {t("of")} {filtered.length}
      </p>

      <div className="mt-8">
        {filtering ? (
          <ContentGridSkeleton count={6} />
        ) : filtered.length ? (
          <ContentGrid
            items={shown}
            base={base}
            locale={locale}
            emptyLabel={emptyLabel}
            logoMode={logoMode}
          />
        ) : (
          <EmptyState
            icon={query || year !== "all" || category !== "all" ? <SearchX /> : <Newspaper />}
            title={query || year !== "all" || category !== "all" ? t("Nothing matches your filters") : emptyLabel}
            hint={
              query || year !== "all" || category !== "all"
                ? t("Try a different search term, switch the year, or reset the filters below.")
                : t("New content is on the way — please check back soon.")
            }
            action={
              query || year !== "all" || category !== "all" ? (
                <Button
                  type="button"
                  onClick={() => {
                    setQuery("");
                    setYear("all");
                    setCategory("all");
                  }}
                >
                  {t("Clear filters")}
                </Button>
              ) : undefined
            }
          />
        )}
      </div>

      {visible < filtered.length ? (
        <div className="mt-10 flex justify-center">
          <Button type="button" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
            {t("Load more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
